import { PaymentAccountingRow, ReceiptApi } from 'common/types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactToPrint, { useReactToPrint } from 'react-to-print';
import HeaderBar from './components/HeaderBar';
import {
	AccountingTableName,
	PaymentWithTransactions,
	ReportData,
	ReportDataType,
	TableSection,
} from './types';
import {
	allAccountingTables,
	allTableSections,
	isAccountingGroupFilterApplied,
	othersAccountingGroup,
	paymentDataToPaymentAccountingRow,
} from './utils';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as StockSelectors from 'selectors/StockSelectors';
import * as UserSelectors from 'selectors/UserSelectors';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';
import { notNull } from 'common/utils/common';
import { useTranslation } from 'services/localization';
import { Divider, Theme, Typography } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import PaymentReceipt from 'components/PaymentReceipt';
import ReportInfo from './components/ReportInfo';
import AccountingTable from './components/AccountingTable';
import FeeTable from './components/FeeTable';
import { api } from 'common/frontend/api';
import useShopFormat from 'hooks/useShopFormat';
import ItemReportTable from './components/LineItemExportTable';
import TransactionReportTable from './components/TransactionsExportTable';
import ContainerBox from 'common/components/layout/ContainerBox';

interface Props {
	report: ReportData;
	startDate: moment.Moment;
	endDate: moment.Moment;
}

const Report = ({ report, startDate, endDate }: Props) => {
	const { t } = useTranslation();
	const classes = useStyles();
	const reportRef = useRef(null);
	const { localFormat } = useShopFormat();
	const receiptRef = useRef<HTMLDivElement | null>(null);
	const shopPublicInfo = useSelector(ShopSelectors.activeShopPublicInfo)!;
	const shopLocations = useSelector(ShopSelectors.activeShopAllLocations);
	const shopCurrency = useSelector(ShopSelectors.getShopCurrency);
	const userLocations = useSelector(UserSelectors.userLocations);
	const customAccountingGroups = useSelector(ShopSelectors.customAccountingGroups);
	const stockProductIdAccountingGroupMap = useSelector(
		StockSelectors.stockProductIdAccountingGroupMap,
	);
	const [receiptToPrint, setReceiptToPrint] = useState<ReceiptApi | null>(null);
	const handlePrint = useReactToPrint({
		content: () => reportRef.current,
		pageStyle: "@page: { size: 'auto', margin: '20mm'}",
	})!;
	const activeLocation = useSelector(UserSelectors.userActiveLocation)!;

	const hasAllLocations = useSelector(UserSelectors.userHasAccessToAllLocations);
	const [currency, setCurrency] = useState<string>(shopCurrency.code);
	const [locationId, setLocationId] = useState<string>(hasAllLocations ? 'all' : activeLocation.id);

	const [tablesToShow, setTablesToShow] = useState<AccountingTableName[]>(
		allAccountingTables.slice(),
	);
	const [tableSectionsToShow, setTableSectionsToShow] = useState<TableSection[]>(
		allTableSections.slice(),
	);
	const allAccountingGroups = [...customAccountingGroups, othersAccountingGroup(t)];
	const [accountingGroupIdsToShow, setAccountingGroupIdsToShow] = useState<string[]>(
		allAccountingGroups.map((group) => group.id),
	);

	// If accounting groups filters are applied, do not show fees or method_breakdown sections
	const accountingGroupFilterApplied = isAccountingGroupFilterApplied(
		accountingGroupIdsToShow,
		allAccountingGroups,
	);
	const tablesToShowFiltered = !accountingGroupFilterApplied
		? tablesToShow
		: tablesToShow.filter((table) => table !== 'fees');
	const tableSectionsToShowFiltered = !accountingGroupFilterApplied
		? tableSectionsToShow
		: tableSectionsToShow.filter((section) => section !== 'method_breakdown');

	const shopLocationString =
		locationId === 'all'
			? t('common:shopLocation.allStores')
			: userLocations.find((l) => l.id === locationId)?.name ?? locationId;

	const filteredReportData: ReportData = useMemo(
		() =>
			Object.entries(report).reduce((tot, [type, paymentWithTransactionsList]) => {
				const paymentWithTransactionsListFiltered = paymentWithTransactionsList.filter(
					({ payment }) =>
						payment.amount.currency.toLocaleUpperCase() === currency.toLocaleUpperCase() &&
						(locationId === 'all' || payment.locationId === locationId),
				);
				return {
					...tot,
					[type]: paymentWithTransactionsListFiltered,
				};
			}, {} as ReportData),
		[currency, locationId, report],
	);

	const paymentAccountingRows: PaymentAccountingRow[] = useMemo(() => {
		return Object.entries(filteredReportData)
			.flatMap(([key, paymentWithTransactionsList]) => {
				const reportType = key as ReportDataType;
				return paymentWithTransactionsList.map((item) =>
					paymentDataToPaymentAccountingRow(
						item,
						reportType,
						customAccountingGroups,
						stockProductIdAccountingGroupMap,
						accountingGroupIdsToShow,
						t,
					),
				);
			})
			.filter(notNull);
	}, [
		accountingGroupIdsToShow,
		customAccountingGroups,
		filteredReportData,
		stockProductIdAccountingGroupMap,
		t,
	]);

	useEffect(() => {
		if (receiptToPrint) {
			const printButton = document.getElementById('print-report-receipt-button');
			if (printButton) {
				printButton.click();
			}
		}
	}, [receiptToPrint]);

	const paymentsData = Object.values(report)
		.flat()
		.map((data: PaymentWithTransactions) => data.payment);

	const availableCurrencies = [
		...new Set([
			shopCurrency.code.toLocaleUpperCase(),
			...paymentsData.map((data) => data.amount.currency.toLocaleUpperCase()),
		]),
	];

	const filteredPaymentData = Object.values(filteredReportData).flat();

	const startReceiptPrint = async (receiptId: string) => {
		const receipt = await api().receipts.doc(receiptId).get();
		if (!!receipt) {
			setReceiptToPrint(receipt);
		}
	};

	const getReportExportHeaders = (reportName: string) => {
		const dateString = `${t(
			'reports.accounting.reportTitle',
			'Rentle report for period',
		)}: ${localFormat(startDate!, 'DD.MM.YYYY')} - ${localFormat(endDate!, 'DD.MM.YYYY')}`;
		const companyString = `${t('common:company', 'Company')}: ${shopPublicInfo.name} ${
			!!shopPublicInfo.businessId && `(${shopPublicInfo.businessId})`
		}`;
		const locationString = `${t('common:shopLocation.location')}: ${shopLocationString}`;
		return [dateString, companyString, locationString, reportName];
	};

	const getReportExportFileName = (reportName: string) => {
		const dateFormat = `${startDate.format('YYYYMMDD')}_${endDate.format('YYYYMMDD')}`;
		return `${dateFormat}_${reportName.toLocaleLowerCase()}`;
	};

	const bookingsTable = (
		<AccountingTable
			rows={paymentAccountingRows.filter((row) => row.type === 'BOOKING')}
			name={t('reports.accounting.bookings.title', 'Bookings')}
			description={t(
				'reports.accounting.bookings.description',
				'Bookings contains all paid orders within the selected time period (both sales and reservations)',
			)}
			tableSectionsToShow={tableSectionsToShowFiltered}
			startReceiptPrint={startReceiptPrint}
			getExportHeaders={getReportExportHeaders}
			getExportFileName={getReportExportFileName}
		/>
	);
	const salesTable = (
		<AccountingTable
			rows={paymentAccountingRows.filter((row) => row.type === 'SALES')}
			name={t('reports.accounting.sales.title', 'Sales')}
			description={t(
				'reports.accounting.sales.description',
				'Order is marked to sales (revenue recognition) on the first day of usage period',
			)}
			tableSectionsToShow={tableSectionsToShowFiltered}
			startReceiptPrint={startReceiptPrint}
			getExportHeaders={getReportExportHeaders}
			getExportFileName={getReportExportFileName}
		/>
	);
	const refundsTable = (
		<AccountingTable
			rows={paymentAccountingRows.filter((row) => row.type === 'REFUND')}
			name={t('reports.accounting.refunds.title', 'Refunds')}
			tableSectionsToShow={tableSectionsToShowFiltered}
			startReceiptPrint={startReceiptPrint}
			getExportHeaders={getReportExportHeaders}
			getExportFileName={getReportExportFileName}
		/>
	);
	const feeTable = (
		<FeeTable
			rows={paymentAccountingRows.filter((row) => row.type === 'BOOKING')}
			startReceiptPrint={startReceiptPrint}
			getExportHeaders={getReportExportHeaders}
			getExportFileName={getReportExportFileName}
		/>
	);

	return (
		<div ref={reportRef} className={classes.reportContainer}>
			<HeaderBar
				availableCurrencies={availableCurrencies}
				currency={currency}
				setCurrency={setCurrency}
				locationId={locationId}
				setLocationId={setLocationId}
				tablesToShow={tablesToShowFiltered}
				setTablesToShow={setTablesToShow}
				tableSectionsToShow={tableSectionsToShowFiltered}
				setTableSectionsToShow={setTableSectionsToShow}
				accountingGroupIdsToShow={accountingGroupIdsToShow}
				setAccountingGroupIdsToShow={setAccountingGroupIdsToShow}
				printReport={handlePrint}
			/>
			<ReportInfo
				startDate={startDate}
				endDate={endDate}
				shopPublicInfo={shopPublicInfo}
				shopLocationString={shopLocations.length > 1 ? shopLocationString : null}
			/>
			{!paymentAccountingRows.length ? (
				<Typography variant="body1" align="center" sx={{ pt: 2 }}>
					{t('reports.accounting.noDataFound', 'No report data found for the selected period')}
				</Typography>
			) : (
				<>
					{tablesToShowFiltered.includes('bookings') && bookingsTable}
					<div className={classes.pageBreak} />
					{tablesToShowFiltered.includes('sales') && salesTable}
					<div className={classes.pageBreak} />
					{tablesToShowFiltered.includes('refunds') && refundsTable}
					<div className={classes.pageBreak} />
					{tablesToShowFiltered.includes('fees') && feeTable}
					<ContainerBox padding="small" sx={{ '@media print': { display: 'none' } }}>
						<Typography variant="h6" sx={{ mb: 1 }}>
							{t('reports.accounting.dataExports', 'Data exports')}
						</Typography>
						<TransactionReportTable
							data={filteredPaymentData}
							getExportFileName={getReportExportFileName}
						/>
						<Divider />
						<ItemReportTable
							data={filteredPaymentData}
							getExportFileName={getReportExportFileName}
						/>
					</ContainerBox>
				</>
			)}
			{receiptToPrint && (
				<>
					<ReactToPrint
						trigger={() => (
							<button id="print-report-receipt-button" style={{ display: 'none' }}>
								{t('common:actions.print')}
							</button>
						)}
						content={() => receiptRef.current!}
						onAfterPrint={() => setReceiptToPrint(null)}
					/>
					<div style={{ display: 'none' }}>
						<PaymentReceipt receipt={receiptToPrint} ref={receiptRef as any} />
					</div>
				</>
			)}
		</div>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		reportContainer: {
			position: 'relative',
			padding: theme.spacing(4, 0),
		},
		pageBreak: {
			display: 'none',
			'@media print': {
				marginTop: '1rem',
				display: 'block',
				pageBreakBefore: 'auto',
			},
		},
	}),
);

export default Report;
